import { useState } from 'react'
import type { ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import {
  canSee,
  isStaff,
  useAnnouncementTicker,
  useChallenge,
  cohortLabel,
  useCommunityFeed,
  homeRoomFor,
  roomAudienceLabel,
  useRoomPosts,
  plainBody,
  useCourseCertificates,
  closestToDone,
  useCrew,
  useAgeBands,
  useDestinations,
  useEvents,
  useJourney,
  useKeepContent,
  useLogChallengeDay,
  usePhraseOfDay,
  useMyCohort,
  useProgress,
  useRecommendations,
  useRooms,
  useShareContent,
  useToggleRsvp,
  ownedIds,
  rsvpRefusal,
  useMyPurchases,
  type Entitlement,
  type Event as EventPayload,
  type PlatformRole,
  type ProgressItem,
  type Recommendation,
} from '@lb/domain'
import { Trans, useTranslation } from '@lb/i18n'
import {
  Button,
  ContentCard,
  ContentCardSkeleton,
  EmptyState,
  IconAnchor,
  IconArrowUpRight,
  IconAward,
  IconBell,
  IconBoat,
  IconCalendar,
  IconChevronRight,
  IconClock,
  IconHeart,
  IconLeaf,
  IconMessage,
  IconRobot,
  IconSearch,
  IconSparkle,
  IconStar4,
  LogoMark,
  Pill,
  PhotoCover,
  Skeleton,
} from '@lb/ui'

import { http } from '../../lib/client'
import { EventActionControl, RsvpRefusalLine, rsvpStateFor } from '../events/events'
import { SEARCH_INPUT_PROPS } from '../../lib/search-input'
import { MemberName } from '../../lib/member-name'
import { useContentLocale } from '../../lib/content-locale'
import { useViewerAudience } from '../../lib/audience'
import {
  formatMonthDay,
  formatRelativeTime,
  hourInMontreal,
  todayISOInMontreal,
  todayInMontreal,
} from '../../lib/format'
import { useSessionContext } from '../../app/providers/session'
import { PurchasesStrip } from './purchases-strip'
import { PremiumLock, isLocked } from '../../lib/paywall'
import { routeForContent, routePathForContent } from '../content-routes'
import { ProgressRing } from '../journey/journey'

/**
 * Home (spec §4).
 *
 * ── Why every row owns its own query ──────────────────────────────────────
 * Home aggregates from seven sources. One `Promise.all` would make the slowest
 * of them the speed of the page, and one failure would blank all seven. So each
 * section fetches independently and renders its own skeleton, its own error and
 * its own empty — the section-level boundary rule (§6.6), and Home is the
 * screen it exists for.
 */
export function HomeScreen() {
  const { t, i18n } = useTranslation()
  const { viewer } = useSessionContext()
  if (!viewer) return null

  const firstName = viewer.session.givenName
  const staff = isStaff(viewer.session)
  const showsCrew = canSee(viewer.session.platformRole, 'crew', staff)
  const showsJourney = canSee(viewer.session.platformRole, 'journey', staff)
  const showsChallenge = canSee(viewer.session.platformRole, 'challenge', staff)
  const showsCertificates = canSee(viewer.session.platformRole, 'certificateStrip', staff)

  return (
    <div className="flex flex-col gap-gap-sec">
      <Ticker />

      {/*
       * `.hero` — eyebrow, greeting, sub-line, ring, AND the phrase strip.
       *
       * The strip belongs INSIDE the hero (`app.jsx:422-459`), not as the next
       * sibling down the page. It was a sibling, which put a full section gap
       * between the greeting and the line that finishes it — the phrase read as
       * the first item of the feed rather than as part of the welcome, and the
       * hero ended on a number.
       */}
      <section className="flex flex-col gap-5 pt-7">
        <header className="flex flex-wrap items-center justify-between gap-6">
          <div className="max-w-[64ch]">
            {/* `.hero-eyebrow` — today's date. Small, but it is what makes the
             * greeting a moment rather than a template. */}
            <p className="mb-3.5 inline-flex items-center gap-2 whitespace-nowrap text-[11.5px] font-semibold uppercase tracking-[0.14em] text-slate">
              <IconSparkle size={13} title={null} className="text-ink" />
              {todayInMontreal(i18n.language)}
            </p>

            {/*
             * The name is emphasised inside the sentence, so this cannot be a
             * plain interpolation — the markup sits mid-string and the position
             * of the name differs by language. <Trans> keeps the sentence one
             * translatable unit instead of three concatenated fragments.
             *
             * The name itself takes NO styling of its own — see `MemberName`
             * (branding decision D5).
             */}
            {/*
             * Greet by name when there is one, and greet anyway when there is
             * not.
             *
             * `givenName` is non-nullable by contract but can be the **empty
             * string**, and there is a live path to it: accepting an
             * organisation seat creates the account from an address alone, so
             * `fullName` is `''` until somebody fills it in and the derivation
             * has nothing to derive from. Interpolating that gives "Good
             * morning, ." to a person on their first visit.
             *
             * A separate key rather than a conditional fragment, because a
             * greeting without a name is a different sentence and its
             * punctuation is the translator's to place.
             */}
            <h1 className="font-serif text-[clamp(26px,3vw,36px)] font-medium leading-[1.08] tracking-[-0.015em] text-ink">
              {firstName ? (
                <Trans
                  i18nKey="home.greeting"
                  values={{ greeting: t(greetingKey()), name: firstName }}
                  components={{ 1: <MemberName /> }}
                />
              ) : (
                t('home.greetingNoName', { greeting: t(greetingKey()) })
              )}
            </h1>

            <DaysAboard />
          </div>
          {showsJourney ? <JourneyRing /> : null}
        </header>

        {/*
         * The phrase of the day is off the member's Home for now — the client's
         * call of 21 Sept 2026: phrases stay an editor's tool in the back
         * office, not something members see. `PhraseCard` stays so a later
         * "bring it back" is one line, and the notification pipe still works.
         */}
        {SHOW_PHRASE_OF_DAY ? <PhraseCard /> : null}
      </section>

      {/*
       * The cohort strip — TSP only, and the design gives it the row directly
       * under the phrase (`tsp-app.jsx`).
       *
       * It is the single most useful thing on a TSP member's home: which month
       * they are in, what the next milestone is, and how far through the
       * milestones they are. We had none of it — a TSP member's Home was a
       * parent's Home with a different nav.
       */}
      {/*
       * « Vos achats » — a single-purchase member's own things, directly under
       * the greeting, where the design puts them. Renders nothing at all for
       * everybody else, including every subscriber.
       */}
      <PurchasesStrip />

      <CohortStrip />

      {/*
       * Two rows, not one that switches.
       *
       * `app.jsx:1215-1217` renders `ContinueRow` and `ContentRow` as separate
       * blocks with the crew board between them, in every state. We had folded
       * them into a single section that showed resume OR recommendations, which
       * meant "For you today" — a whole named section of the design — existed
       * nowhere, and a returning member with history never saw a recommendation
       * at all.
       */}
      <ContinueRow />

      {showsCrew ? <CrewBoard /> : null}

      {/*
       * The certifications strip sits between the two content rows on TSP's
       * home (`tsp-app.jsx:401`) — the same slot a parent's crew board takes,
       * which is the design putting "where do I stand" between "what was I
       * doing" and "what should I look at".
       *
       * TSP only, and that is a placement rather than a statement about who
       * earns certificates: `ProApp`'s home does not render `CertStrip` even
       * though a Pro member holds the same credentials.
       */}
      {showsCertificates ? <CertStrip /> : null}

      <ContentRow />

      {showsChallenge ? <ChallengeCard /> : null}

      <CommunityAndComingUp />

      <CoachCard />

      <HomeFooter />
    </div>
  )
}

/**
 * `.canvas-foot` — the logo mark and one line, at the bottom of Home.
 *
 * Small, and the design puts it there for a reason: "Hébergé au Canada · Loi
 * 25" is a Québec product telling a parent where their family's data lives,
 * on the screen they open every day rather than buried in a policy. The
 * hosting claim and the law are translated as a pair; only the surrounding
 * copy differs by language.
 *
 * ── The product name in front of it is the ROLE's ─────────────────────────
 * All three prototypes end on this line and all three name a different
 * product: `La Boussole` for a parent, `La Boussole Pro` for a practitioner,
 * and `La Boussole · TSP` for a cohort member (`app.jsx:1226`,
 * `pro-app.jsx:542`, `tsp-app.jsx:407`). We shipped the parent's to everyone,
 * so a practitioner's last line of the day told them they were somewhere else.
 * Kept in the bundle rather than composed here — `La Boussole · TSP` carries
 * its own separator, and building that out of a name and a tag in code would
 * make the middot a layout decision instead of part of the name.
 */
function HomeFooter() {
  const { t } = useTranslation()
  const { viewer } = useSessionContext()
  const role = viewer?.session.platformRole ?? 'PARENT'

  return (
    <footer className="flex items-center justify-center gap-2 pt-2 text-[12px] text-muted">
      <LogoMark size={18} />
      <span>{t('home.footerNote', { product: t(`home.footerProduct.${role}`) })}</span>
    </footer>
  )
}

/** Time-aware, in the platform's timezone rather than the device's. */
function greetingKey(): string {
  const hour = hourInMontreal()
  if (hour < 12) return 'home.greetingMorning'
  if (hour < 18) return 'home.greetingAfternoon'
  return 'home.greetingEvening'
}

/**
 * The announcement ticker.
 *
 * It LINKS, to `/announcements`. The gated route is
 * `GET /announcements/{id}` — but nothing here needs it: the ticker payload is
 * `MemberAnnouncement`, which carries the body, so the list page renders in
 * full and there is no detail route to open.
 *
 * 🔴 Not dismissible. `tickerUntil` is a GLOBAL window — the ticker shows for
 * everybody or nobody — and there is no per-user dismissal (round 3 §15). A
 * dismiss button that reappeared on reload would be worse than none, so there
 * is none.
 */
function Ticker() {
  const { t } = useTranslation()
  const locale = useContentLocale()
  const ticker = useAnnouncementTicker(http, locale)
  const announcement = ticker.data?.items[0]
  if (!announcement) return null
  /*
   * The API answers in the requested locale when it can and falls back to the
   * announcement's source when a translation is missing (`response.locale`
   * names what came back). On an English home a French-only announcement used
   * to render as French with no signal — reads like the interface silently
   * swapped languages. A short chip in front of the title makes the fallback
   * visible: same bar, marked as the other language.
   */
  // The response envelope's `locale` is the locale the CLIENT asked for — it
  // always matches, so it can never signal a fallback. Compare against the
  // item's `sourceLocale` (what it was AUTHORED in): if the two differ, the
  // API resolved the title from another locale and the strip is showing the
  // wrong-language line.
  const servedLocale = announcement.sourceLocale
  const fellBack = servedLocale.toLowerCase() !== locale.toLowerCase()

  return (
    /*
     * `.ann-ticker` — a full-width INK pill with a quiet amande tag and an
     * amande CTA, not a lila block of text.
     *
     * Lila is the announcements *category* tint, which is why it was reached
     * for; but the design uses the tints to colour-code content and reserves
     * ink for the one strip that must interrupt. As a pale lila block it read
     * as another card in the stack and was skipped.
     */
    <Link
      to="/announcements"
      /*
       * `bg-ink text-cream` in both themes (accent policy: no lilac surface
       * outside the primary button); in dark the bar is amande with navy text.
       */
      className="flex w-full items-center gap-3.5 rounded-full bg-ink py-2.5 pe-3.5 ps-4.5 text-cream transition-transform hover:-translate-y-px"
    >
      <span className="inline-flex flex-none items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.06em] text-cream opacity-80">
        <IconBell size={13} title={null} />
        {t('home.announcementTag')}
      </span>
      <span className="min-w-0 flex-1 truncate text-[14px] font-medium">
        {fellBack ? (
          // A solid pill, not a translucent tint over ink — the previous
          // `bg-cream/20` on `bg-ink` read as an ink-on-ink smear at 10px
          // and the "FR" mark was effectively invisible next to a French
          // title on an English home, which was the whole point of adding
          // the chip. A solid amande mark with pinned navy text on the
          // `bg-ink` bar.
          <span
            data-testid="fallback-locale-chip"
            aria-label={servedLocale}
            className="me-2 inline-flex items-center rounded-sm bg-cream px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em] text-db-ink"
          >
            {servedLocale.toUpperCase()}
          </span>
        ) : null}
        {announcement.title}
      </span>
      <span className="inline-flex flex-none items-center gap-0.5 text-[12.5px] font-semibold text-cream">
        <span className="hidden sm:inline">{t('home.announcementRead')}</span>
        <IconChevronRight size={14} title={null} />
      </span>
    </Link>
  )
}

/**
 * `.hero-sub` — "You've been aboard for 184 days."
 *
 * Reads the same `useJourney` query as the ring beside it, so this is a cache
 * hit rather than a second request. Renders nothing until it has a number: a
 * greeting followed by a skeleton bar is worse than a greeting alone.
 */
function DaysAboard() {
  const { t } = useTranslation()
  const { viewer } = useSessionContext()
  const journey = useJourney(http)
  if (!journey.data) return null

  const days = journey.data.daysAboard
  const firstName = viewer?.session.givenName ?? ''

  /*
   * "You've been aboard for 0 days" reads oddly — reported. Same field, a
   * different sentence for the first-visit case: name the reader and offer
   * an invitation, not a count against zero. Any positive day count uses
   * the pluralised message unchanged.
   */
  return (
    <p className="mt-2 max-w-[50ch] text-[14.5px] text-muted">
      {days === 0
        ? t('home.daysAboardZero', { name: firstName })
        : t('home.daysAboard', { count: days })}
    </p>
  )
}

function JourneyRing() {
  const { t } = useTranslation()
  const journey = useJourney(http)
  if (!journey.data) return <Skeleton className="h-[92px] w-[92px] rounded-full" />

  return (
    /*
     * `.journey-badge` — the ring, and under it the name of the place it goes.
     *
     * The prototype frames the number as `124 / 300`; our contract's `score` is
     * 0–100, so the denominator would be invented. The LABEL is not invented,
     * and it is the half that made the ring a destination rather than an
     * ornament — it was an unlabelled circle you had to guess was clickable.
     */
    <Link
      to="/journey"
      aria-label={t('home.journeyLabel')}
      className="group flex flex-col items-center gap-1.5"
    >
      <ProgressRing value={journey.data.score} />
      <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-slate transition-colors group-hover:text-ink">
        <IconBoat size={13} title={null} className="text-ink" />
        {t('home.myJourney')}
      </span>
    </Link>
  )
}

/**
 * The anchoring phrase.
 *
 * Rose doux in BOTH themes with dark text — the handoff's canonical case of a
 * tint surface that stays light while the neutral tokens flip, so the ink is
 * pinned rather than inherited.
 */
const SHOW_PHRASE_OF_DAY = false

function PhraseCard() {
  const { t } = useTranslation()
  const locale = useContentLocale()
  const phrase = usePhraseOfDay(http, locale)
  const keep = useKeepContent(http)
  const share = useShareContent(http)
  /*
   * Local, and it has to be.
   *
   * `POST /content/{id}/save` answers with `ContentSignals` — three counts —
   * and no content read carries a per-caller "did I keep this" flag. So the
   * pressed state is only knowable within a session; it does not survive a
   * reload, and pretending otherwise would mean inventing a fact. Noted for a
   * later ask rather than faked.
   */
  const [kept, setKept] = useState(false)

  if (phrase.isPending) return <Skeleton className="h-24 w-full" />
  // No phrase scheduled today is not an error and not an empty state — it is
  // simply a row that does not appear.
  if (phrase.isError || !phrase.data.phrase) return null

  return (
    /*
     * `.phrase-strip` — a slim ROW: anchor glyph, the line, and its actions.
     * It was a tall stacked figure, which gave a single sentence the visual
     * weight of a card and pushed everything below it off the fold.
     */
    <figure className="flex items-center gap-4 rounded-[var(--r-lg)] bg-cream-dark px-[18px] py-4">
      <span className="grid h-[38px] w-[38px] flex-none place-items-center rounded-full bg-ink/10 text-ink">
        <IconAnchor size={18} title={null} />
      </span>
      <blockquote className="min-w-0 flex-1 font-serif text-[18px] italic leading-[1.4] text-ink">
        {phrase.data.phrase.text}
        {phrase.data.phrase.attribution ? (
          <cite className="mt-1 block text-[13px] not-italic text-ink">
            — {phrase.data.phrase.attribution}
          </cite>
        ) : null}
      </blockquote>

      {/*
       * `.phrase-strip-actions` — keep and share, 36px discs on a translucent
       * white wash. The strip had neither, so the one line the design invites a
       * parent to hold on to had nothing to hold it with.
       *
       * A phrase is a content item, which is why these are the same two
       * endpoints every other keepable thing uses — `POST /content/{id}/save`
       * is documented on the backend as « Kept in heart », the design's own
       * phrase for this control.
       */}
      <div className="flex flex-none items-center gap-1.5">
        <button
          type="button"
          aria-pressed={kept}
          aria-label={t('home.phraseKeep')}
          title={t('home.phraseKeep')}
          onClick={() => {
            const next = !kept
            setKept(next)
            keep.mutate(
              { id: phrase.data.phrase!.id, keep: next },
              // Put the button back if the write failed — a heart that stays
              // filled on a failed request is a promise the server did not make.
              { onError: () => setKept(!next) },
            )
          }}
          className="grid h-9 w-9 place-items-center rounded-full bg-white/60 text-ink-on-tint transition-colors hover:bg-white/90"
        >
          <IconHeart size={15} title={null} fill={kept ? 'currentColor' : 'none'} />
        </button>
        <button
          type="button"
          aria-label={t('home.phraseShare')}
          title={t('home.phraseShare')}
          onClick={() => {
            const text = phrase.data.phrase!.text
            share.mutate(phrase.data.phrase!.id)
            /*
             * The native sheet where there is one, the clipboard where there is
             * not. Both are best-effort: a member who dismisses the share sheet
             * rejects the promise, and that is a cancellation rather than a
             * failure worth reporting.
             */
            if (navigator.share) {
              void navigator.share({ text }).catch(() => {})
            } else {
              void navigator.clipboard?.writeText(text).catch(() => {})
            }
          }}
          className="grid h-9 w-9 place-items-center rounded-full bg-white/60 text-ink-on-tint transition-colors hover:bg-white/90"
        >
          <IconArrowUpRight size={15} title={null} />
        </button>
      </div>
    </figure>
  )
}

/**
 * `.destination-chips` — "Tuned to your destination: «Sleep» «Tantrums»".
 *
 * The rail hands back `destinationIds`; the names live in the taxonomy, so this
 * joins the two. `useDestinations` is a 30-minute-stale shared query, so the two
 * rows that draw chips cost one request between them.
 *
 * ── The label and the chips are one unit ─────────────────────────────────
 * They have to be, and this is the bug that proved it: with the label in the
 * caller and the chips here, a member whose rail carries no destinations —
 * anyone who skipped the questionnaire, which is `parent1` — got the sentence
 * "Tuned to your destination:" ending in a colon with nothing after it. So the
 * component owns the whole sub-line and falls back to a sentence that stands on
 * its own rather than one that trails off.
 */
function TunedSub({ ids, fallback }: { ids: readonly string[]; fallback: string }) {
  const { t } = useTranslation()
  const locale = useContentLocale()
  const audience = useViewerAudience()
  const destinations = useDestinations(http, locale, audience)

  const byId = new Map((destinations.data?.items ?? []).map((d) => [d.id, d.label]))
  const names = ids.map((id) => byId.get(id)).filter((n): n is string => Boolean(n))

  if (names.length === 0) return <>{fallback}</>

  return (
    <>
      {t('home.tunedTo')}{' '}
      <span className="inline-flex flex-wrap gap-1.5 align-middle">
        {names.map((name) => (
          <em
            key={name}
            /* `.destination-chips em` — a neutral bleu-gris wash, 12px/600
             navy; the wash follows the theme so `text-ink` is right. */
            className="rounded-full bg-bleu-gris-light px-[9px] py-0.5 text-[12px] font-semibold not-italic text-ink-on-tint"
          >
            {name}
          </em>
        ))}
      </span>
    </>
  )
}

/** `.block-head` — heading, sub-line, and the link on the right. */
function SectionHead({
  title,
  sub,
  linkTo,
  linkLabel,
}: {
  title: string
  sub: ReactNode
  linkTo: string
  linkLabel: string
}) {
  return (
    /* `.block-head` — `align-items: flex-end`, `gap: 18px`, `margin-bottom: 22px`. */
    <div className="mb-[22px] flex flex-wrap items-end justify-between gap-[18px]">
      <div className="min-w-0 flex-1">
        {/*
         * `.block-head h2` — `clamp(22px, 2.6vw, 28px)` at weight 500, and it
         * was a flat 24px at the browser's default 400. Two steps down from
         * the design at every width above ~920px, on the heading that opens
         * every section of the page.
         */}
        <h2 className="font-serif text-[clamp(22px,2.6vw,28px)] font-medium leading-[1.15] tracking-[-0.01em] text-ink">
          {title}
        </h2>
        <p className="mt-1 text-[13.5px] text-muted">{sub}</p>
      </div>
      {/*
       * `.block-link` — a PILL, not bare text: `padding: 6px 10px`,
       * `border-radius: 99px`, `--ink`, and `--cream-dark` on hover. It read as
       * a dimmer piece of body copy because it was `ink-soft` with no box.
       * The padding is also what gives it a real target without a `min-h`
       * prop-up.
       */}
      <Link
        to={linkTo}
        className="inline-flex flex-none items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-1.5 text-[13px] font-semibold text-ink transition-colors hover:bg-cream-dark"
      >
        {linkLabel}
        <IconChevronRight size={14} title={null} />
      </Link>
    </div>
  )
}

/**
 * `.continue-block` — "Continue where you left off", or "Recommended for you".
 *
 * ── Two states, and the design says which is which ────────────────────────
 * `app.jsx:585` builds this from one flag: history or no history. With history
 * it is "Continue where you left off" over what you actually started; without,
 * "Recommended for you" over a first step. Heading, sub-line and header link
 * all swap together — three strings, one condition.
 *
 * ── Unfinished only, most recent first ────────────────────────────────────
 * `completed: false` is asked of the SERVER rather than filtered here, so
 * `total` describes the unfinished set and page one is not mostly things you
 * have already finished.
 *
 * ── Three cards, capped ───────────────────────────────────────────────────
 * The mock draws three and so does this. The no-history branch used to render
 * the recommendation rail UNCAPPED — seventeen cards on a fresh account, the
 * entire catalogue under a heading promising a first step.
 */
function ContinueRow() {
  const locale = useContentLocale()
  const { t } = useTranslation()
  const { viewer } = useSessionContext()
  const recommendations = useRecommendations(http, locale)
  const progress = useProgress(http, { completed: false, pageSize: 6 })

  const resume = progress.data?.items ?? []
  /*
   * The recommendation rail is NOT waited on when there is history.
   *
   * Both queries fire together, so gating the resume row on the slower of the
   * two would hold back the row a returning member came for, in order to be
   * ready to render the row they will not see.
   */
  const hasHistory = resume.length > 0

  if (hasHistory) {
    return (
      <section>
        <SectionHead
          title={t('home.continueTitle')}
          sub={t('home.continueSub')}
          linkTo="/resources"
          linkLabel={t('home.myLibrary')}
        />
        <div className="grid gap-gap-card sm:grid-cols-2 lg:grid-cols-3">
          {resume.slice(0, 3).map((item) => (
            <ResumeCard key={item.contentItemId} item={item} />
          ))}
        </div>
      </section>
    )
  }

  /*
   * `STARTER_PICKS` in the mock is its own fixture. Here both rows draw on the
   * one recommendation rail, so this takes the first three and `ContentRow`
   * takes the next three — the alternative is the same three cards printed
   * twice under two different headings.
   */
  const picks = recommendations.data?.items.slice(0, 3) ?? []

  return (
    <section>
      <SectionHead
        title={t('home.recommendedTitle')}
        sub={
          <TunedSub
            ids={recommendations.data?.destinationIds ?? []}
            fallback={t('home.recommendedSubPlain')}
          />
        }
        linkTo="/resources"
        linkLabel={t('home.seeWholeLibrary')}
      />

      {recommendations.isPending ? (
        <div className="grid gap-gap-card sm:grid-cols-2 lg:grid-cols-3">
          <ContentCardSkeleton />
          <ContentCardSkeleton />
          <ContentCardSkeleton />
        </div>
      ) : recommendations.isError ? (
        <EmptyState title={t('home.recoErrorTitle')} body={t('home.recoErrorBody')} />
      ) : picks.length === 0 ? (
        /*
         * Empty is legitimate — "nothing to go on" when the questionnaire was
         * skipped, which the contract says explicitly. Not an error.
         */
        <EmptyState
          title={t('home.recoEmptyTitle')}
          body={t('home.recoEmptyBody')}
          action={
            <Link
              to="/resources"
              className="inline-flex min-h-[24px] items-center text-[14px] font-semibold text-ink underline"
            >
              {t('home.openLibrary')}
            </Link>
          }
        />
      ) : (
        /*
         * `.content-grid` — three across, not a horizontal rail.
         *
         * A rail is right for a long shelf you browse sideways; this is three
         * suggestions, and as a scroller the third one sat off-screen on every
         * viewport we render. The design uses the grid here and keeps rails for
         * the library.
         */
        <div className="grid gap-gap-card sm:grid-cols-2 lg:grid-cols-3">
          {picks.map((item) => (
            <RecommendationCard
              key={item.id}
              item={item}
              entitlement={viewer?.entitlement ?? 'unknown'}
              /* The no-history branch replaces the time-left line with an
                 invitation — `.continue-start` in the mock. */
              action={t('home.startNow')}
            />
          ))}
        </div>
      )}
    </section>
  )
}

/**
 * `.block` — "For you today" (`ContentRow`, app.jsx:631).
 *
 * A separate section from `ContinueRow` and not a fallback for it: the mock
 * renders both at once, and they answer different questions — "what were you in
 * the middle of" against "what should you look at now".
 *
 * Drawn from the same rail as `ContinueRow`, and deliberately disjoint from it
 * in both directions. The mock keeps two fixtures and never has to think about
 * this; one rail feeding two rows does.
 *
 *   · No history — `ContinueRow` shows the first three picks, so this starts
 *     after them.
 *   · History — `ContinueRow` shows progress instead, so the whole rail is
 *     free, EXCEPT that a recommendation may be the very thing being resumed.
 *     Measured: "Orchidée" appeared as the resume card and again two rows down
 *     under "For you today". Anything already in progress is filtered out.
 */
function ContentRow() {
  const locale = useContentLocale()
  const { t } = useTranslation()
  const { viewer } = useSessionContext()
  const recommendations = useRecommendations(http, locale)
  const progress = useProgress(http, { completed: false, pageSize: 6 })

  const resumed = new Set((progress.data?.items ?? []).map((p) => p.contentItemId))
  const hasHistory = resumed.size > 0
  const offset = hasHistory ? 0 : 3
  const items = (recommendations.data?.items ?? [])
    .filter((item) => !resumed.has(item.id))
    .slice(offset, offset + 3)

  // Nothing left over once the row above took its three: the section is not
  // empty, it does not apply. An "empty state" here would be a heading over a
  // box explaining that a rail we already rendered is short.
  if (!recommendations.isPending && items.length === 0) return null

  return (
    <section>
      <SectionHead
        title={t('home.forYouToday')}
        sub={
          <TunedSub
            ids={recommendations.data?.destinationIds ?? []}
            fallback={t('home.forYouSub')}
          />
        }
        linkTo="/resources"
        linkLabel={t('home.seeWholeLibrary')}
      />

      {recommendations.isPending ? (
        <div className="grid gap-gap-card sm:grid-cols-2 lg:grid-cols-3">
          <ContentCardSkeleton />
          <ContentCardSkeleton />
          <ContentCardSkeleton />
        </div>
      ) : (
        <div className="grid gap-gap-card sm:grid-cols-2 lg:grid-cols-3">
          {items.map((item) => (
            <RecommendationCard
              key={item.id}
              item={item}
              entitlement={viewer?.entitlement ?? 'unknown'}
            />
          ))}
        </div>
      )}
    </section>
  )
}

/**
 * One card out of the recommendation rail, shared by both rows above.
 *
 * ── The why-line is degraded, deliberately ────────────────────────────────
 * The mock's `.cc-why` names the reason — "For your destination · Sleep", "New
 * in the library". `Recommendation` carries no reason field, only `pinned`, so
 * the most this can say truthfully is that an answer named it. The rest of the
 * sentence is asked of the backend (`1-parent-home-data-request.md` §2); until
 * it lands, an unpinned card shows no why-line rather than a guessed one.
 *
 * ── And so is the meta line ───────────────────────────────────────────────
 * The mock's is a duration ("4 modules · 38 min"). The rail carries no duration
 * either, so `summary` takes the slot: a sentence about the thing, which is at
 * least true, where a fabricated running time would not be.
 */
function RecommendationCard({
  item,
  entitlement,
  action,
}: {
  item: Recommendation
  entitlement: Entitlement
  action?: string
}) {
  const { t } = useTranslation()
  const locked = isLocked(item, entitlement)
  const href = routeForContent(item.path, item.id)
  const why = useWhyLine(item)

  const card = (
    <ContentCard
      title={item.title}
      /* `.cc-why` — why this card is in front of you. */
      eyebrow={why}
      type={t(`contentType.${item.type}`)}
      /* Home's pill is the ink one (styles.css:940); the Library's is cream. */
      pillTone="ink"
      summary={item.summary}
      action={action}
      playable
      /* A course's square thumbnail, whole, on the navy it was drawn on (its
         ground is the surface-ink token to the hex) — the rail mixes formats. */
      cover={
        <PhotoCover
          src={item.coverUrl}
          seed={item.id}
          ratio="16/10"
          fit={item.type === 'COURSE' ? 'contain' : 'cover'}
          tone={item.type === 'COURSE' ? 'navy' : 'amande'}
        />
      }
      href={locked || !href ? undefined : href}
    />
  )

  /*
   * Locked, not left out (3 Oct 2026): the library is subscribers-only and the
   * rail now recommends premium to everybody, so a reader without a
   * subscription sees what is there and the one way in — the same overlay the
   * Library draws.
   */
  return locked ? (
    <PremiumLock item={item} reason={t('resources.locked')}>
      {card}
    </PremiumLock>
  ) : (
    card
  )
}

/**
 * `.cc-why` — the reason line, composed HERE rather than served as a sentence.
 *
 * The API hands back a discriminated `reason` precisely so the wording is the
 * client's job: a string minted server-side could only be in one language, and
 * this is a French-first product with an English translation.
 *
 * ── What each kind means ─────────────────────────────────────────────────
 * `AGE_BAND.primary` separates the band an item was WRITTEN for from one it
 * stretches to — "Best for 3–5" against "Also good for 3–5". They score
 * differently in the rail, so collapsing them would label the order by
 * something it is not ordered on.
 *
 * `NEW` marks the padding tail the backend appends when the personalised set
 * is short. It is append-only, so a matched card is never behind an unmatched
 * one and this line always reads truthfully down the row.
 *
 * `PILLAR` resolves to nothing: there is no pillars taxonomy hook, and a line
 * naming a pillar we cannot name would be worse than no line. It falls through
 * to undefined and the card simply has no why-row.
 */
function useWhyLine(item: Recommendation): string | undefined {
  const { t } = useTranslation()
  const locale = useContentLocale()
  const audience = useViewerAudience()
  const destinations = useDestinations(http, locale, audience)
  const ageBands = useAgeBands(http, locale)

  const reason = item.reason
  // A rail predating the field: `pinned` was the only signal we had.
  if (!reason) return item.pinned ? t('home.chosenForYou') : undefined

  switch (reason.kind) {
    case 'PINNED':
      return t('home.chosenForYou')
    case 'NEW':
      return t('home.whyNew')
    case 'DESTINATION': {
      const name = destinations.data?.items.find((d) => d.id === reason.destinationId)?.label
      return name ? t('home.whyDestination', { name }) : undefined
    }
    case 'AGE_BAND': {
      const band = ageBands.data?.items.find((b) => b.id === reason.ageBandId)?.label
      if (!band) return undefined
      return reason.primary ? t('home.whyAgeBest', { band }) : t('home.whyAgeAlso', { band })
    }
    default:
      return undefined
  }
}

/**
 * `.defi-card` — the mock's rich weekly-challenge card.
 *
 * Cover artwork on the left, body on the right — the cover comes from
 * `ChallengePeriod.coverUrl` (a signed URL minted per read, nullable when
 * an editor authored a period without imagery). No image = a plain
 * two-column card, deliberately, because the daily prompt is what does
 * the work; the picture is atmosphere.
 *
 * The strip below the title states "Day N of 7 · streak M" — the same
 * fact `t('home.challengeDayOf')` and `t('home.challengeStreak')`
 * pluralise, so 0/7 is legitimate. "For today" is anchored below and
 * links into `/challenge` to log — the actual write route belongs on
 * that screen, not on the home strip.
 */
function ChallengeCard() {
  const { t } = useTranslation()
  const challenge = useChallenge(http, useContentLocale())
  const log = useLogChallengeDay(http)
  const [coverFailed, setCoverFailed] = useState(false)
  if (challenge.isPending) return <Skeleton className="h-40 w-full rounded-[var(--r-lg)]" />
  if (challenge.isError || !challenge.data) return null

  const period = challenge.data
  const done = period.days.filter((d) => d.logged).length
  const streak = period.streak.current

  return (
    <section>
      <div className="flex flex-col overflow-hidden rounded-[var(--r-xl)] border border-border bg-surface md:flex-row">
        {/*
         * The week's own cover when the team set one; otherwise the La Boussole
         * compass star on the brand's navy (8 Oct 2026). The bundled fallback
         * was a generic accessibility-style figure that `object-cover` blew up
         * to fill the panel — and no live week has a cover yet. A signed URL
         * that fails (it expires after a few minutes) drops to the star too.
         */}
        <div /* `.defi-art` — a panel 280px wide, not 220. */
          className="relative h-40 flex-none overflow-hidden bg-surface-ink md:h-auto md:w-[280px]"
        >
          {period.coverUrl && !coverFailed ? (
            <img
              src={period.coverUrl}
              alt=""
              loading="lazy"
              className="h-full w-full object-cover"
              onError={() => setCoverFailed(true)}
            />
          ) : (
            <div aria-hidden className="grid h-full w-full place-items-center text-on-surface-ink">
              <LogoMark size={88} className="opacity-90" />
            </div>
          )}
        </div>

        {/* `.defi-body` — `--pad-card-lg`, 16px between rows. */}
        <div className="flex min-w-0 flex-1 flex-col gap-4 p-card-lg">
          {/* `.defi-eyebrow` — and the "Full challenge" link is part of it,
              pushed to the far edge with `margin-left: auto`. */}
          {/* Wraps as two whole pieces on a phone (9 Oct 2026): the eyebrow,
              then « Full challenge » on its own — never a link broken in two. */}
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-ink">
            <IconCalendar size={13} title={null} />
            <span className="min-w-0">
              {period.theme
                ? t('home.challengeTaglineTheme', { theme: period.theme })
                : t('home.challengeTagline')}
            </span>
            <Link
              to="/challenge"
              className="ms-auto inline-flex items-center gap-0.5 whitespace-nowrap text-[12px] font-semibold normal-case tracking-normal text-ink hover:underline"
            >
              {t('home.challengeFull')}
              <IconChevronRight size={13} title={null} />
            </Link>
          </p>

          <h2 className="text-balance font-serif text-[clamp(24px,2.6vw,30px)] font-medium leading-[1.15] tracking-[-0.01em] text-ink">
            {period.title}
          </h2>
          {period.description ? (
            <p className="max-w-[56ch] whitespace-pre-line text-[14.5px] text-muted">
              {period.description}
            </p>
          ) : null}

          {/* `.defi-progress` — an 8px bar over the day count and the streak. */}
          <div className="flex flex-col gap-2">
            <div
              className="h-2 overflow-hidden rounded-full bg-progress-track"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={period.days.length}
              aria-valuenow={done}
            >
              <span
                className="block h-full rounded-full bg-progress transition-[width] duration-700"
                style={{
                  inlineSize: `${Math.round((done / Math.max(1, period.days.length)) * 100)}%`,
                }}
              />
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 text-[12.5px] text-muted">
              <span>
                <strong className="font-semibold text-ink">
                  {t('home.challengeDay', { count: done })}
                </strong>{' '}
                {t('home.challengeOf', { total: period.days.length })}
              </span>
              {streak > 0 ? (
                <span className="inline-flex items-center gap-1 font-semibold text-ink">
                  {t('home.challengeStreak', { count: streak })}
                  <IconLeaf size={14} title={null} />
                </span>
              ) : null}
            </div>
          </div>

          {/*
           * `.defi-today` — the inner cream panel, and the whole point of the
           * card being on Home rather than a link to the challenge screen.
           *
           * It was missing: the card asked nothing and offered a single button
           * that navigated away. The mock puts the day's question here with
           * both answers beside it, so the interaction the challenge exists for
           * completes without leaving the page.
           *
           * Hidden once today is logged — the panel is a prompt, and a prompt
           * you have already answered is just a form asking again.
           */}
          {/*
           * Shown whenever today is not logged — it used to need a daily prompt,
           * so a week written without one (every week the client wrote) had no
           * button on Home at all. Today's own line, else the week's, else a
           * question that fits any challenge.
           */}
          {!period.streak.loggedToday ? (
            <div className="flex flex-col gap-3 rounded-[var(--r)] bg-canvas px-5 py-[18px]">
              <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate">
                {t('home.challengeForToday')}
              </p>
              <p className="font-serif text-[18px] italic leading-[1.3] text-ink">
                {period.days.find((day) => day.state === 'now')?.prompt ??
                  period.dailyPrompt ??
                  t('home.challengeTodayPrompt')}
              </p>
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => log.mutate({})} disabled={log.isPending}>
                  {t('home.challengeDidToday')}
                  <IconChevronRight size={14} title={null} />
                </Button>
                {/*
                 * "Not today" is a LINK, not a write. There is no endpoint for
                 * declining a day and inventing one would record a refusal the
                 * product never asked for — an unlogged day is already what
                 * "not today" means. It opens the week instead.
                 */}
                <Link
                  to="/challenge"
                  className="inline-flex min-h-[40px] items-center rounded-full border border-border bg-surface px-[18px] text-[13px] font-semibold text-ink-soft transition-colors hover:border-border-deep hover:text-ink"
                >
                  {t('home.challengeNotToday')}
                </Link>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  )
}

/**
 * `.crew-board` — the home page's My Crew widget.
 *
 * Renders each child as a card. Per-child *activity* would say "Sleep ·
 * Module 2 · watched 2 days ago" on the mock, but neither
 * `ContentProgress` nor `ModuleProgress` carries a `childId` today, so the
 * "who is watching" concept doesn't exist on the wire and any per-child
 * line would be a lie. Every child card shows the "Ready to start" chip
 * (the mock's own first-visit variant) with a link into the library —
 * honest, and matches the mock exactly for the empty-progress case. The
 * co-parent card sits alongside as a "manage sharing" link.
 *
 * Not rendered when the caller has no crew at all — a heading with an
 * empty grid reads as broken; the sidebar CrewPanel already prompts to
 * add children, and duplicating that CTA on home would clutter the page.
 */
function CrewBoard() {
  const { t, i18n } = useTranslation()
  const crew = useCrew(http)
  // The family photo (11 Oct 2026) — its own field now, set from the profile.
  const familyPhotoUrl = useSessionContext().viewer?.session.familyPhotoUrl ?? null
  if (crew.isPending) return <Skeleton className="h-40 w-full rounded-[var(--r-lg)]" />

  const children = crew.data?.children ?? []
  const coParent = crew.data?.coParent
  if (children.length === 0 && !coParent) return null

  return (
    <section>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-center gap-4">
          {familyPhotoUrl ? (
            <img
              src={familyPhotoUrl}
              alt=""
              data-testid="home-family-photo"
              className="h-[64px] w-[96px] flex-none rounded-[var(--r)] object-cover"
            />
          ) : null}
          <div>
            <h2 className="font-serif text-[clamp(22px,2.6vw,28px)] font-medium leading-[1.15] tracking-[-0.01em] text-ink">
              {t('home.crewHomeTitle')}
            </h2>
            <p className="mt-1 text-[13.5px] text-muted">{t('home.crewHomeSub')}</p>
          </div>
        </div>
        <Link
          to="/profile"
          className="inline-flex flex-none items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-1.5 text-[13px] font-semibold text-ink transition-colors hover:bg-cream-dark"
        >
          {t('home.crewManage')}
          <IconChevronRight size={14} title={null} />
        </Link>
      </div>

      {/*
       * `.crew-cards` — `repeat(3, 1fr)`, dropping to 2 at 1100px and 1 at
       * 640px (styles.css:537-541). Nothing count-dependent.
       *
       * ── What was here, and why it was broken ─────────────────────────────
       * A pair of conditional overrides tried to stop a lone card stretching
       * across three columns: `sm:grid-cols-1 sm:max-w-md` at one card,
       * `lg:grid-cols-2` at two. But `lg:grid-cols-3` from the base list is
       * never cancelled by an `sm:` class — a wider media query wins whatever
       * the class order — so a single child got THREE columns inside a 406px
       * box and rendered as a 122px card with "Ready to start" truncated to
       * "Re...". Measured on parent1 with one child.
       *
       * The mock has no such rule because it does not need one: at 1fr each,
       * one card occupies a third and the other two thirds stay empty, which
       * is the correct answer and was the thing being worked around.
       */}
      <div className="grid grid-cols-1 gap-gap-card sm:grid-cols-2 min-[1101px]:grid-cols-3">
        {children.map((c) => {
          const age = c.birthYear != null ? new Date().getFullYear() - c.birthYear : null
          return (
            /*
             * `.crew-card` — the whole card is the target, not a control
             * tucked inside it. The mock makes it a button with `role` and a
             * keydown handler; a Link gets all of that from the platform.
             *
             * Where it goes is the honest part: the mock resumes the child's
             * own last thing, which needs per-child progress the contract does
             * not carry yet (`1-parent-home-data-request.md` §1). Until it
             * does, the card opens the library rather than pretending to know
             * where this particular child left off.
             */
            <Link
              key={c.id}
              to="/resources"
              className="group flex items-center gap-[13px] rounded-[var(--r-lg)] border border-border bg-surface px-3.5 py-3 transition-[border-color,box-shadow] hover:border-border-deep hover:shadow-[0_14px_30px_-24px_rgb(31_45_58/0.35)]"
            >
              {c.photoUrl ? (
                <img
                  src={c.photoUrl}
                  alt=""
                  className="h-[52px] w-[52px] flex-none rounded-full object-cover shadow-[0_0_0_3px_var(--cream-dark)]"
                />
              ) : (
                <span
                  aria-hidden
                  /* A neutral bleu-gris wash for the initial disc. */
                  className="grid h-[52px] w-[52px] flex-none place-items-center rounded-full bg-bleu-gris-light font-serif text-[22px] text-ink-on-tint shadow-[0_0_0_3px_var(--cream-dark)]"
                >
                  {c.name.trim().charAt(0).toUpperCase() || '?'}
                </span>
              )}
              {/* `.crew-card-id` */}
              <div className="min-w-0 flex-1">
                {/* `.crew-card-name` is the SERIF face at 17/500, with the age
                    beside it on the baseline — not sans semibold. */}
                <p className="flex items-baseline gap-2 font-serif text-[17px] font-medium text-ink">
                  <span className="truncate">{c.name}</span>
                  {age != null ? (
                    <span className="flex-none whitespace-nowrap font-sans text-[12px] font-medium text-muted">
                      {t('crew.age', { count: age })}
                    </span>
                  ) : null}
                </p>
                {/*
                 * `.crew-last` — "Orchidée · Module 2 · watched 2 days ago",
                 * or the first-visit hint for a child with no attributed
                 * session yet.
                 *
                 * The module part is composed HERE from `moduleOrdinal` /
                 * `moduleTotal` rather than served as a label: "Module 2" is
                 * English and this product is written in French first, so a
                 * server-minted string could not be translated by the client
                 * drawing it. Both are null on a type with no modules — an
                 * audio shows its title alone, not a phantom count.
                 */}
                <p className="mt-1 truncate text-[12.5px] text-slate">
                  {c.lastActivity ? (
                    <>
                      <strong className="font-semibold text-ink">
                        {c.lastActivity.moduleOrdinal != null
                          ? // `n`, not `ordinal` — i18next reserves `ordinal`
                            // for ordinal plural rules and types it as boolean.
                            t('home.crewLastModule', {
                              title: c.lastActivity.title,
                              n: c.lastActivity.moduleOrdinal,
                            })
                          : c.lastActivity.title}
                      </strong>
                      {' · '}
                      {formatRelativeTime(c.lastActivity.at, i18n.language)}
                    </>
                  ) : (
                    t('home.crewFirstVisit')
                  )}
                </p>
              </div>
              {/*
               * `.crew-go` — the round affordance on the card's right edge,
               * filling with ink on hover. A CHEVRON rather than the mock's
               * play triangle: play promises to resume this child's own item,
               * and that is the line we cannot draw yet.
               */}
              <span
                aria-hidden
                className="grid h-[30px] w-[30px] flex-none place-items-center rounded-full bg-cream-dark text-ink transition-colors group-hover:bg-ink group-hover:text-cream"
              >
                <IconChevronRight size={13} title={null} />
              </span>
            </Link>
          )
        })}

        {coParent ? (
          <Link
            to="/profile"
            /* `.coparent-card` — same card, dashed border on the cream fill. */
            className="group flex items-center gap-[13px] rounded-[var(--r-lg)] border border-dashed border-border-deep bg-cream-dark px-3.5 py-3 transition-colors hover:bg-cream-darker"
          >
            <span
              aria-hidden
              /* `.crew-coparent-avatar` — a WHITE disc with a dashed ring, not
                 a bare outline: it reads as an empty seat beside the filled
                 photo discs, which is what a co-parent invitation is. */
              className="grid h-[52px] w-[52px] flex-none place-items-center rounded-full border-[1.5px] border-dashed border-border-deep bg-white font-serif text-[22px] text-slate"
            >
              {coParent.trim().charAt(0).toUpperCase() || '+'}
            </span>
            <div className="min-w-0 flex-1">
              <p className="flex items-baseline gap-2 font-serif text-[17px] font-medium text-ink">
                <span className="truncate">{coParent}</span>
                <span className="flex-none whitespace-nowrap font-sans text-[12px] font-medium text-muted">
                  {t('crew.coParent')}
                </span>
              </p>
              {/* `--muted`, not `--slate`: this sub-line sits on `--cream-dark`
                  rather than on a white card, where slate measures 4.35:1 and
                  misses AA. `--muted` is 4.71:1 on that exact surface, which
                  the tokens file records. */}
              <p className="mt-1 truncate text-[12.5px] text-muted">
                {t('home.crewManageSharing')}
              </p>
            </div>
            <span
              aria-hidden
              className="grid h-[30px] w-[30px] flex-none place-items-center rounded-full bg-cream-dark text-ink transition-colors group-hover:bg-ink group-hover:text-cream"
            >
              <IconChevronRight size={13} title={null} />
            </span>
          </Link>
        ) : null}
      </div>
    </section>
  )
}

/**
 * The two-column "community + coming up" block.
 *
 * The community feed reads only from rooms the caller has joined —
 * unjoined rooms would flood a first-time visitor with practitioner-
 * authored threads meant for the /community browse page. Coming up reads
 * from the upcoming events list (same endpoint /events?when=upcoming).
 *
 * Empty states are named rather than hidden — a member who has joined no
 * rooms sees a prompt to browse; a period with no live sessions gets a
 * short "nothing yet" line, which is more honest than the block
 * disappearing.
 */
function CommunityAndComingUp() {
  const { t, i18n } = useTranslation()
  const locale = useContentLocale()
  const feed = useCommunityFeed(http)
  const events = useEvents(http, 'upcoming', locale)
  const { viewer } = useSessionContext()
  /*
   * A post carries `roomId` but not the room's name or audience, so the chips
   * come from the rooms list. It is a shared, long-lived query the Community
   * screen already fetches — a cache hit here rather than a second request.
   */
  const rooms = useRooms(http, locale)
  const roomsById = new Map((rooms.data?.items ?? []).map((r) => [r.id, r]))
  /*
   * A Parent sees the parents' « salle communautaire », a Pro the
   * practitioners' one (8 Oct 2026) — the room everyone of that type is in.
   * Anybody else keeps the feed of every room they follow.
   */
  const homeRoom = viewer ? homeRoomFor(rooms.data?.items ?? [], viewer.session.platformRole) : null
  const roomPosts = useRoomPosts(http, homeRoom?.id)
  const posts = homeRoom ? roomPosts.data?.items : feed.data?.items
  const postsPending = homeRoom ? roomPosts.isPending : feed.isPending || rooms.isPending

  return (
    <div className="grid gap-gap-sec lg:grid-cols-2">
      <section>
        <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="font-serif text-[clamp(22px,2.6vw,28px)] font-medium leading-[1.15] tracking-[-0.01em] text-ink">
              {t('home.communityTitle')}
            </h2>
            {feed.data ? (
              <p className="mt-0.5 text-[13px] text-muted">
                {t('home.communityRoomsFollowing', { count: feed.data.roomsFollowing })}
                {/*
                 * "· ~1 member online". Approximate on purpose, and the mock
                 * writes the tilde: this is a recency count derived from
                 * `last_seen_at` over a window the response carries with it,
                 * not live presence — nothing here holds a socket, so somebody
                 * who closed the tab five minutes ago is still counted. It
                 * counts PEOPLE, not memberships.
                 */}
                {feed.data.membersOnline ? (
                  <> · {t('home.communityOnline', { count: feed.data.membersOnline.count })}</>
                ) : null}
              </p>
            ) : null}
          </div>
          <Link
            to="/community"
            className="inline-flex flex-none items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-1.5 text-[13px] font-semibold text-ink transition-colors hover:bg-cream-dark"
          >
            {t('home.communityAllRooms')}
            <IconChevronRight size={14} title={null} />
          </Link>
        </div>

        {postsPending ? (
          <Skeleton className="h-40 w-full rounded-[var(--r-lg)]" />
        ) : (posts?.length ?? 0) === 0 ? (
          <EmptyState title={t('home.communityEmpty')} body="" />
        ) : (
          <div className="flex flex-col divide-y divide-border-soft rounded-[var(--r-lg)] border border-border bg-surface">
            {posts!.slice(0, 3).map((p) => {
              const initial = p.author.fullName.trim().charAt(0).toUpperCase()
              const room = p.roomId ? roomsById.get(p.roomId) : undefined
              const hearts = Object.values(p.reactions).reduce((sum, n) => sum + n, 0)
              return (
                <Link
                  key={p.id}
                  to="/community/$roomId"
                  params={{ roomId: p.roomId ?? '' }}
                  className="flex gap-3.5 px-5 py-[18px] transition-colors hover:bg-canvas"
                >
                  {p.author.photoUrl ? (
                    <img
                      src={p.author.photoUrl}
                      alt=""
                      aria-hidden
                      className="h-10 w-10 flex-none rounded-full bg-bleu-gris-light object-cover"
                    />
                  ) : (
                    <span
                      aria-hidden
                      className="grid h-10 w-10 flex-none place-items-center rounded-full bg-bleu-gris-light text-[13px] font-semibold text-ink-on-tint"
                    >
                      {initial}
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    {/*
                     * `.cm-post-head` — WHICH ROOM, who it is for, and how long
                     * ago. The author's name was the only thing here, which is
                     * the least useful of the three: on a feed drawn from every
                     * room a member follows, the room is the context that makes
                     * a stray sentence make sense.
                     */}
                    <div className="mb-1 flex flex-wrap items-center gap-2 text-[11px]">
                      {/* `.post-room` — `--cream-dark` with `--ink`. This and
                          the audience chip below had their fills swapped, so
                          the room read as the tinted one and the audience as
                          the neutral, inverting the mock's emphasis. */}
                      {room ? (
                        <span className="rounded-full bg-cream-dark px-[9px] py-0.5 font-semibold text-ink">
                          {room.name}
                        </span>
                      ) : null}
                      {/*
                       * One chip for who the room is for. A room open to more
                       * than one audience is "Mixed" rather than a row of
                       * chips — the mock's own label, and three stacked chips
                       * would crowd out the room name they qualify.
                       */}
                      {room && room.audiences.length > 0 ? (
                        <span className="rounded-full bg-bleu-gris-light px-[9px] py-0.5 text-[11px] font-semibold text-ink-on-tint">
                          {roomAudienceLabel(room.audiences)
                            ? t(`community.audience.${roomAudienceLabel(room.audiences)}`)
                            : t('community.audience.MIXED')}
                        </span>
                      ) : null}
                      <span className="text-muted">
                        {formatRelativeTime(p.createdAt, i18n.language)}
                      </span>
                    </div>

                    {/* `.post-body` — 14px `--ink`. It is the thing a member
                        actually came to read; ink-soft made it the quietest
                        line in a card whose chips were louder. */}
                    <p className="line-clamp-2 text-[14px] leading-[1.5] text-ink">
                      {plainBody(p.body)}
                    </p>

                    {/*
                     * `.cm-post-foot` — reaction and comment counts, then Reply.
                     *
                     * Counts only, no reaction buttons: this row is a link into
                     * the thread, and nesting a control inside it would make one
                     * press mean two things. Reacting happens where the post is.
                     */}
                    <div className="mt-2 flex items-center gap-4 text-[12px] text-muted">
                      <span className="inline-flex items-center gap-1">
                        <IconHeart size={12} title={null} />
                        {hearts}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <IconMessage size={12} title={null} />
                        {p.commentCount}
                      </span>
                      <span className="ms-auto font-semibold text-ink-soft">
                        {t('home.communityReply')}
                      </span>
                    </div>
                  </div>
                </Link>
              )
            })}
          </div>
        )}

        {/*
         * `Post in a room` — the dashed full-width button under the feed.
         *
         * Rendered whether or not the feed has anything in it: a member with no
         * rooms is exactly the one who needs the way in, and the mock's empty
         * state is a prompt to write rather than a dead panel.
         */}
        <Link
          to={homeRoom ? '/community/$roomId' : '/community'}
          params={homeRoom ? { roomId: homeRoom.id } : undefined}
          className="mt-3.5 flex w-full items-center justify-center gap-2 rounded-[var(--r)] border border-dashed border-border-deep bg-surface p-3.5 text-[13.5px] font-semibold text-ink transition-colors hover:border-ink-soft hover:bg-canvas"
        >
          <span aria-hidden className="text-[15px] leading-none">
            +
          </span>
          {t('home.communityPost')}
        </Link>
      </section>

      <section>
        <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="font-serif text-[clamp(22px,2.6vw,28px)] font-medium leading-[1.15] tracking-[-0.01em] text-ink">
              {t('home.comingUpTitle')}
            </h2>
            <p className="mt-0.5 text-[13px] text-muted">{t('home.comingUpSub')}</p>
          </div>
          <Link
            to="/events"
            className="inline-flex flex-none items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-1.5 text-[13px] font-semibold text-ink transition-colors hover:bg-cream-dark"
          >
            {t('home.comingUpCalendar')}
            <IconChevronRight size={14} title={null} />
          </Link>
        </div>

        {events.isPending ? (
          <Skeleton className="h-40 w-full rounded-[var(--r-lg)]" />
        ) : (events.data?.items.length ?? 0) === 0 ? (
          <EmptyState title={t('home.comingUpEmpty')} body="" />
        ) : (
          <div className="flex flex-col gap-3">
            {events.data!.items.slice(0, 2).map((ev) => (
              <EventRow key={ev.id} event={ev} />
            ))}
          </div>
        )}
      </section>
    </div>
  )
}

/**
 * One event on Home (ISS-08, ISS-16, ISS-19 — 13 Oct 2026).
 *
 * ── It never read `locked`, and both failures came out of that ───────────
 * The button was rendered unconditionally. On a **premium** event the server
 * refuses the write — `EVENT_NOT_FOUND`, measured — and with no error surface
 * the press did nothing at all: ISS-19's « the buttons don't respond ». On a
 * free one it succeeds, which is ISS-08, and the four items the report calls
 * permission leaks are five rows published with `premium = false`, which is
 * the operator's decision and not a client rule to re-impose.
 *
 * So: **read `locked`, never recompute it.** `isLocked` takes the row's own
 * answer, and the fallback it keeps is for a server that sends none. The trap
 * this avoids is live on mobile today — `shouldLock(entitlement, premium)`
 * cannot tell a course its owner paid for from one they cannot open, so it
 * paywalls the thing they bought.
 *
 * ── And say so when it is refused ────────────────────────────────────────
 * `rsvpStateFor` + `RsvpRefusalLine` are `events.tsx`'s, and the reason they
 * are shared rather than copied is the bug `events.tsx` already fixed: one
 * mutation serves every row, so its error belongs to the row it was fired
 * from. Home renders two rows and would otherwise put the refusal under both.
 */
function EventRow({ event }: { event: EventPayload }) {
  const { t, i18n } = useTranslation()
  const { viewer } = useSessionContext()
  const rsvp = useToggleRsvp(http)
  /* Shared query key with the Home strip and the rail card — one request. */
  const purchases = useMyPurchases(http, viewer?.entitlement !== 'entitled')
  const start = new Date(event.startsAt)
  const day = start.getDate()
  const month = start.toLocaleDateString(i18n.language, { month: 'short' }).toUpperCase()
  const time = start.toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })
  const daysUntil = Math.max(0, Math.round((start.getTime() - Date.now()) / (24 * 60 * 60 * 1000)))

  const minutes = event.endsAt
    ? Math.round((new Date(event.endsAt).getTime() - start.getTime()) / 60_000)
    : null
  const metaParts = [
    event.author?.fullName,
    event.author?.credential,
    t(`events.kind.${event.kind}`),
    minutes && minutes > 0 ? t('home.comingUpMinutes', { count: minutes }) : null,
  ].filter((part): part is string => Boolean(part))

  const { busy, error } = rsvpStateFor(rsvp, event.id)
  const refusal = rsvpRefusal(error)
  const bought = ownedIds(purchases.data).has(event.id)

  /*
   * `Sign me up` is still the mock's one filled control here — but it is the
   * SAME control the events list and the event page draw, because the branch
   * behind it had been written three times and this was the copy that was
   * wrong: no `locked` case, so a premium event offered a write the server
   * refuses with nothing said (ISS-08, ISS-19), and no `bought` case, so a
   * place somebody had PAID for came with a « Je ne pourrai pas » button.
   */
  const action = (
    <EventActionControl
      event={event}
      bought={bought}
      locked={isLocked(event, viewer?.entitlement)}
      onRsvp={(on) => rsvp.mutate({ eventId: event.id, on })}
      busy={busy}
      size="sm"
      signUpLabel={t('home.comingUpSignUp')}
    />
  )

  return (
    <div className="flex flex-col gap-2 rounded-[var(--r-lg)] border border-border bg-surface p-card">
     <div className="flex gap-4">
      {/*
       * Date pill — inverted `--surface-inverted` navy in dark mode, the
       * month quieter than the day, per mock lines 1840-1842. `bg-ink` in dark
       * would flip to cream and pin the day/month as cream-on-cream,
       * which is what dev showed.
       */}
      {/*
       * `.event-date` — the day at 26px in the numeric face, the month
       * quieter, and the time under a hairline rule. It was three flat lines
       * at 18/10/10 with no separator, which read as a stacked label rather
       * than as the calendar block the design draws.
       */}
      <div className="flex flex-col items-center justify-center rounded-[var(--r)] bg-ink px-1.5 py-3 text-center text-cream dark:bg-surface-inverted dark:text-ink">
        <span className="font-num text-[26px] font-bold leading-none">{day}</span>
        <span className="mt-0.5 text-[10.5px] font-semibold uppercase tracking-[0.1em] opacity-80">
          {month}
        </span>
        <span className="mt-2 w-full border-t border-[rgb(248_242_234/0.12)] pt-1.5 font-num text-[11.5px] text-[rgb(248_242_234/0.6)]">
          {time}
        </span>
      </div>
      <div className="min-w-0 flex-1">
        {/*
         * `.event-main h3` — 17px SERIF, not 15px semibold sans.
         *
         * ISS-16: the control used to sit at the end of the wrapping chip row
         * with `ms-auto`, so its position moved with the chip text and the
         * summary's length — three cards, three different places. The report
         * asks for it right-aligned on the TITLE row, and that is also where
         * it stops moving.
         */}
        <div className="mb-1 flex items-start justify-between gap-3">
          <p className="min-w-0 text-balance font-serif text-[17px] font-medium leading-[1.2] tracking-[-0.005em] text-ink">
            <Link to="/events/$id" params={{ id: event.id }} className="hover:underline">
              {event.title}
            </Link>
          </p>
          <div className="flex-none">{action}</div>
        </div>

        {/*
         * `.ev-meta` — "Krysta Letto · M.Sc. · Live · 75 min".
         *
         * Who is running it and how long it takes are the two things that
         * decide whether a parent signs up, and the row carried neither. Every
         * part is optional and dropped when absent rather than left as a
         * stranded separator: an event with no host and no end time falls back
         * to its kind alone.
         */}
        {metaParts.length > 0 ? (
          <p className="mt-1 truncate text-[12.5px] text-muted">{metaParts.join(' · ')}</p>
        ) : null}

        {event.summary ? (
          <p className="mt-0.5 line-clamp-2 text-[13px] text-muted">{event.summary}</p>
        ) : null}

        <div className="mt-2 flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-bleu-gris-light px-2 py-0.5 text-[11.5px] font-semibold text-ink-on-tint">
            {daysUntil <= 0 ? t('events.today') : t('home.comingUpIn', { count: daysUntil })}
          </span>
        </div>
      </div>
     </div>

      {/* The refusal, under the row it came from — never under both. */}
      <RsvpRefusalLine refusal={refusal} />
    </div>
  )
}

/**
 * `.coach-card` — the Mat Bot section at the bottom of the home page.
 *
 * A search chip cluster the parent can click into pre-filled, plus a
 * plain field that submits to `/search?q=…`. Prompts are static: the
 * design shows a hand-picked handful (`COACH_PROMPTS` in the mock),
 * they don't paginate, and a server-side endpoint for them would be
 * three sentences translated once — content lives in the i18n bundle
 * instead of a `phrases`-sized table.
 *
 * ── Three sets, one per role ──────────────────────────────────────────────
 * Each prototype ships its own trio and they are not variations on a theme —
 * `COACH_PROMPTS`, `PRO_PROMPTS` and `TSP_PROMPTS` ask different questions
 * about different people. We shipped the parent's to everyone, so a
 * psychoeducator with a caseload was offered "My child refuses to put on their
 * coat" and "I'm feeling parental burnout". Not merely off-target: the whole
 * card is the product guessing what you came for, and guessing your job wrong
 * is the one guess that undoes it.
 */
const COACH_PROMPTS: Record<PlatformRole, readonly [string, string, string]> = {
  PARENT: ['home.coachPromptCoat', 'home.coachPromptBurnout', 'home.coachPromptTeen'],
  PRO: [
    'home.coachPromptProTransitions',
    'home.coachPromptProMeeting',
    'home.coachPromptProScreening',
  ],
  TSP: [
    'home.coachPromptTspCase',
    'home.coachPromptTspSupervision',
    'home.coachPromptTspTemplates',
  ],
  // No org-admin prototype. They read the Pro surface everywhere else — the
  // nav table maps them onto `PRO_NAV` — so they get its questions too.
  ORG_ADMIN: [
    'home.coachPromptProTransitions',
    'home.coachPromptProMeeting',
    'home.coachPromptProScreening',
  ],
}

function CoachCard() {
  const { t } = useTranslation()
  const { viewer } = useSessionContext()
  const firstName = viewer?.session.givenName ?? ''
  const [q, setQ] = useState('')

  const prompts = COACH_PROMPTS[viewer?.session.platformRole ?? 'PARENT'].map((key) => t(key))

  return (
    <section
      /*
       * `.coach-card` — Vert Boussole EXACT at `--r-xl` with `--pad-card-lg`
       * (branding decision D10: the client asked for this panel's green to be
       * checked against the guide). `--coach-bg` / `--coach-border` carry the
       * two themes: vert with a navy-tinted edge in light, a navy card with a
       * vert edge in dark — see tokens.css for why the dark surface is not
       * green.
       */
      className="rounded-[var(--r-xl)] coach-panel border p-card-lg"
      aria-label={t('home.coachName')}
    >
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:gap-6">
        <span
          aria-hidden
          className="grid h-16 w-16 flex-none place-items-center coach-edge rounded-full border bg-surface"
        >
          <IconRobot size={32} title={null} className="text-ink" />
        </span>

        <div className="flex min-w-0 flex-1 flex-col gap-3">
          {/*
           * `.coach-eyebrow` is TWO things, and we had run them together into
           * one uppercase string joined by a middot.
           *
           * The name is the uppercase tracked-out slate label; `.coach-status`
           * beside it is `text-transform: none`, navy, and led by a 6px
           * navy dot (styles.css:1328-1352). The dot is the point — it says the
           * assistant is there, which a middot cannot.
           *
           * The tagline stays FRENCH in both locales. It reads "Notre robot
           * matelot" in the English prototype next to fully translated copy,
           * which is a deliberate keep: it is the character's name, in the same
           * way the product is not "The Compass".
           */}
          <p className="flex flex-wrap items-center gap-2.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate">
            <IconRobot size={14} title={null} />
            {t('home.coachName')}
            <span className="inline-flex items-center gap-1.5 normal-case tracking-[0.04em] text-ink">
              <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-ink" />
              {t('home.coachTagline')}
            </span>
          </p>
          {/* `.coach-card h2` — the member's name in the sentence's own style,
              exactly as the hero greeting sets it (D5). */}
          <h2 className="font-serif text-[clamp(22px,2.4vw,28px)] font-medium leading-[1.15] tracking-[-0.01em] text-ink">
            <Trans
              i18nKey="home.coachTitle"
              values={{ name: firstName }}
              components={{ 1: <MemberName /> }}
            />
          </h2>
          {/* `.coach-sub` — 14px `--ink-soft`, capped at 60ch. */}
          <p className="max-w-[60ch] text-[14px] leading-[1.55] text-ink-soft">
            {t('home.coachBody')}
          </p>

          <div className="flex flex-wrap gap-2">
            {prompts.map((prompt) => (
              <Link key={prompt} to="/search" search={{ q: prompt }} className="rounded-full">
                <Pill as="span" size="sm" tone="outline" interactive>
                  {prompt}
                </Pill>
              </Link>
            ))}
            <span className="min-h-[36px] rounded-full border border-dashed border-border-deep px-3 text-[13px] font-medium text-muted">
              {t('home.coachPromptElse')}
            </span>
          </div>

          <form
            action="/search"
            method="get"
            className="mt-2 flex h-[44px] items-center gap-2 rounded-full border border-border bg-surface pe-1 ps-4 focus-within:border-ink"
          >
            <input
              name="q"
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('home.coachSearchPlaceholder')}
              {...SEARCH_INPUT_PROPS}
              className="min-w-0 flex-1 bg-transparent text-[14px] outline-none placeholder:text-muted"
            />
            <button
              type="submit"
              aria-label={t('search.submit')}
              className="grid h-9 w-9 flex-none place-items-center rounded-full bg-ink text-cream"
            >
              <IconSearch size={14} title={null} />
            </button>
          </form>
        </div>
      </div>
    </section>
  )
}

/**
 * `.continue-card` — one thing you started (`app.jsx:604`, `styles.css:573`).
 *
 * ── Its own markup, not a `ContentCard` ──────────────────────────────────
 * The mock ships `.continue-card` and `.content-card` as two components, and
 * they differ in the places that matter rather than in decoration: the thumb is
 * 16/9 against 16/10, the crumb sits ABOVE the title where the content card's
 * why-line sits below it behind a dashed rule, the body padding is tighter, and
 * the play affordance appears on hover rather than resting on the artwork.
 *
 * Rendering this through `ContentCard` put "For Léa · Module 2 of 4" — the one
 * line that says who this card is for — underneath the title in the slot meant
 * for a footnote.
 *
 * ── The progress bar renders only where it can be true ────────────────────
 * `positionSeconds` and `durationSeconds` are both nullable, and for good
 * reason: a reading has no timeline. The design draws a bar on every card
 * because its fixture is all video. A bar with no duration behind it would be
 * a claim about how far through you are, invented.
 *
 * So a card without a duration shows no bar and no "x minutes left" — it still
 * says what it is and takes you back to it, which is the point of the row.
 */
function ResumeCard({ item }: { item: ProgressItem }) {
  const { t } = useTranslation()
  // Rendered through `Link`, so the path is app-relative — the basepath
  // carries the locale, and a prefixed `to` renders /en/en/… on English.
  const href = routePathForContent(item.path, item.contentItemId)
  /*
   * `FOR LÉA · MODULE 2 OF 4` — the mock's `.continue-crumb`.
   *
   * `ProgressItem.childId` is the id ONLY; the backend deliberately does not
   * resolve the name, because Home already reads `/me/crew` to draw the crew
   * board and a child's name is the most private thing this API holds — one
   * copy on one response rather than two. So the join happens here, off the
   * cache that read already filled.
   *
   * A session nobody attributed (every session recorded before attribution
   * landed) keeps the plain module count.
   */
  const crew = useCrew(http)
  const childName = item.childId
    ? crew.data?.children.find((c) => c.id === item.childId)?.name
    : undefined

  const timed =
    item.durationSeconds !== null && item.durationSeconds > 0 && item.positionSeconds !== null
  const fraction = timed ? Math.min(1, item.positionSeconds! / item.durationSeconds!) : null
  const leftMinutes = timed
    ? Math.max(1, Math.round((item.durationSeconds! - item.positionSeconds!) / 60))
    : null

  const crumb = [
    childName ? t('home.crumbFor', { name: childName }) : null,
    item.course
      ? t('home.moduleCount', {
          done: item.course.modulesComplete,
          total: item.course.modulesTotal,
        })
      : t('home.inProgress'),
  ]
    .filter(Boolean)
    .join(' · ')

  const Root = href ? Link : 'div'

  return (
    <Root
      {...(href ? { to: href } : {})}
      className="group flex flex-col overflow-hidden rounded-[var(--r-lg)] border border-border bg-surface transition-[transform,border-color,box-shadow] duration-150 hover:-translate-y-0.5 hover:border-border-deep hover:shadow-[0_14px_30px_-24px_rgb(31_45_58/0.35)]"
    >
      {/* `.continue-thumb` — 16/9, and the play sits centred on it. */}
      <div className="relative grid aspect-[16/9] w-full place-items-center overflow-hidden bg-cream-dark">
        <PhotoCover
          src={item.coverUrl}
          seed={item.contentItemId}
          ratio="16/9"
          fit={item.contentType === 'COURSE' ? 'contain' : 'cover'}
          tone={item.contentType === 'COURSE' ? 'navy' : 'amande'}
          className="absolute inset-0"
        />

        {/* `.continue-kind` — 9.5px/700, top-left at 10px. */}
        <span /* `--ink-on-tint`: the pill fill is the mock's literal
                 `rgba(248,242,234,.92)` and does NOT flip, so `--ink` on it
                 went pale-on-pale in dark. Measured 1.10:1. */
          className="absolute left-2.5 top-2.5 rounded-full bg-[rgb(248_242_234/0.92)] px-[9px] py-[3px] text-[9.5px] font-bold uppercase tracking-[0.05em] text-ink-on-tint"
        >
          {t(`contentType.${item.contentType}`)}
        </span>

        {/*
         * `.continue-play` — 46px, dark, and INVISIBLE until the card is
         * hovered. It rests over the artwork rather than in a corner, which is
         * why it can afford to be this large: it is the hover state of the
         * whole card, not a control competing with it.
         */}
        <span
          aria-hidden
          className="relative grid h-[46px] w-[46px] scale-[0.85] place-items-center rounded-full bg-[rgb(41_51_57/0.9)] text-on-surface-ink opacity-0 transition-all duration-150 group-hover:scale-100 group-hover:opacity-100"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <path d="M8 5.2v13.6l11-6.8z" />
          </svg>
        </span>

        {fraction !== null ? (
          /* `.continue-thumb-bar` — 4px across the foot of the artwork. */
          <span aria-hidden className="absolute inset-x-0 bottom-0 block h-1 bg-progress-track">
            <span
              className="block h-full bg-progress"
              style={{ inlineSize: `${Math.round(fraction * 100)}%` }}
            />
          </span>
        ) : null}
      </div>

      {/* `.continue-body` — 14px 16px 16px, tighter than a content card. */}
      <div className="min-w-0 flex-1 px-4 pb-4 pt-3.5">
        {/* `.continue-crumb` — ABOVE the title, uppercase, navy. */}
        <p className="text-[11px] font-semibold uppercase tracking-[0.03em] text-ink">{crumb}</p>
        <h3 className="mt-[5px] line-clamp-2 font-serif text-[18px] font-medium leading-[1.22] text-ink">
          {item.title}
        </h3>
        {leftMinutes !== null ? (
          /* `.continue-meta` — clock in `--slate`, text in `--muted`. */
          <p className="mt-2.5 inline-flex items-center gap-1.5 text-[12.5px] text-muted">
            <IconClock size={12} title={null} className="text-slate" />
            {t('home.minutesLeft', { count: leftMinutes })}
          </p>
        ) : null}
      </div>
    </Root>
  )
}

/**
 * `.pro-cert-strip` — "Your certifications" (`pro-app.jsx:181`).
 *
 * Same shell as the cohort strip below and deliberately so: the design gives
 * both rows one shape — icon, sentence, a dashed-off measure, a way in — and
 * varies only the icon's tint. They are the two "where do I stand" rows of a
 * TSP member's home and reading as a pair is the point.
 *
 * ── The count is the envelope's, never the page's ─────────────────────────
 * `earned` comes off the response rather than out of `items`. Counting the
 * rows would answer "certificates on this page", which is a different number
 * the moment the catalogue outgrows one page — and the member could not tell
 * which one they were being shown.
 *
 * ── Renders even at zero ──────────────────────────────────────────────────
 * `0 certificates earned` over a course three parts from done is the strip
 * doing its job: it explains a mechanism the member has not used yet. It drops
 * out only when the list itself is empty — no practitioner catalogue, nothing
 * to certify, and a sentence about finishing modules that do not exist.
 */
function CertStrip() {
  const { t } = useTranslation()
  const locale = useContentLocale()
  const certificates = useCourseCertificates(http, locale)

  const data = certificates.data
  if (!data || data.items.length === 0) return null

  const next = closestToDone(data.items)

  return (
    <section className="flex flex-wrap items-center gap-5 rounded-[var(--r-lg)] border border-border bg-surface px-6 py-5">
      {/* `.pro-cert-icon` — a bleu-gris mist, like the cohort strip's. */}
      <span
        aria-hidden
        className="grid h-12 w-12 flex-none place-items-center rounded-full bg-bleu-gris-light text-ink-on-tint"
      >
        <IconAward size={20} title={null} />
      </span>

      <div className="min-w-[220px] flex-1">
        <p className="text-[19px] font-bold leading-[1.25] text-ink">{t('certificates.title')}</p>
        <p className="mt-1 text-[13.5px] leading-[1.5] text-slate">
          {t('certificates.earnedBody', { count: data.earned })}
        </p>
      </div>

      {next ? (
        <div className="flex w-full flex-none flex-col gap-1 border-t border-dashed border-border pt-3.5 min-[721px]:w-auto min-[721px]:min-w-[220px] min-[721px]:max-w-[280px] min-[721px]:border-l min-[721px]:border-t-0 min-[721px]:pl-5 min-[721px]:pt-0">
          <p className="text-[11px] font-bold uppercase tracking-[0.05em] text-muted">
            {t('certificates.closest')}
          </p>
          {/* `.pro-cert-next strong` — 13.5px ink, above the bar rather than
              below it, which is what distinguishes this column from the cohort
              strip's otherwise identical one. */}
          <p className="truncate text-[13.5px] font-semibold text-ink">{next.title}</p>
          <span aria-hidden className="block h-1.5 overflow-hidden rounded-full bg-progress-track">
            <span
              className="block h-full rounded-full bg-progress"
              style={{ inlineSize: `${Math.round((next.partsComplete / next.parts) * 100)}%` }}
            />
          </span>
          <p className="text-[12px] text-slate">
            {/* `count` is the DONE half — French inflects « partie » on it and
                English does not, which is why both locales carry both forms. */}
            {t('certificates.parts', { count: next.partsComplete, total: next.parts })}
          </p>
        </div>
      ) : null}

      <Link
        to="/resources"
        className="inline-flex min-h-[44px] flex-none items-center gap-1 rounded-full border border-border px-[18px] text-[13.5px] font-semibold text-ink transition-colors hover:border-ink-soft"
      >
        {t('certificates.allModules')}
        <IconChevronRight size={14} title={null} />
      </Link>
    </section>
  )
}

/**
 * `.pro-cert-strip.tsp-strip` — where you are in the programme, on Home.
 *
 * Renders for anybody whose cohort read answers — the query is `retry: false`
 * and returns `null` for a member with no cohort, so a Pro or a parent gets
 * nothing without this needing to know their role.
 *
 * ── The sub-line is TWO facts, and picking them by `state` was wrong ──────
 * The design writes "**Integration block** · next milestone: Live supervision
 * II, September 4." — the block you are working through, then the next thing
 * with a date on it. We wrote only the second half, and took it as
 * `milestones.find(m => m.state !== 'done')`.
 *
 * That reads the wrong field. `state` is not chronological: the cohort read
 * answers `current` for **three** module blocks at once (Feb, Apr and Jun),
 * because a block is current until every module inside it is complete —
 * nothing about it says which one today falls in. So on 19 August the strip
 * announced "Next milestone: Bloc 1 — Fondations, **Feb 9, 2026**": a date six
 * months behind the member, presented as what is coming.
 *
 * Both halves come off the calendar instead:
 *
 *   · the block is the `MODULE_BLOCK` whose `occursOn…endsOn` window contains
 *     today, falling back to the last one that has started — which is the only
 *     reading of "the block you are in" that a date can support;
 *   · next is the first milestone dated after today, in position order.
 *
 * Either may be absent — a cohort in a gap between blocks, or one whose last
 * dated milestone is behind them — so the line is assembled from what exists
 * and omitted entirely when neither does.
 */
function CohortStrip() {
  const { t } = useTranslation()
  const cohort = useMyCohort(http)

  if (!cohort.data) return null

  const { cohort: programme, month, milestones, milestonesComplete } = cohort.data
  const total = milestones.length

  /*
   * Compared as `YYYY-MM-DD` strings, not as Dates.
   *
   * `occursOn` and `endsOn` are calendar days with no time and no zone, and
   * `new Date('2026-08-28')` is midnight UTC — which is the 27th for every
   * member in this product's timezone, so a block would end a day early for
   * all of them. Lexicographic comparison on ISO days is exact and has no
   * zone to get wrong.
   */
  const today = todayISOInMontreal()
  const blocks = milestones.filter((m) => m.kind === 'MODULE_BLOCK' && m.occursOn <= today)
  const block =
    blocks.find((m) => (m.endsOn ?? m.occursOn) >= today) ?? blocks[blocks.length - 1] ?? null
  const next = milestones.find((m) => m.occursOn > today)

  /*
   * Three whole sentences rather than fragments joined here. A translator
   * given "next milestone: {title}, {day}" as a piece cannot know whether it
   * starts a sentence, and French wants the space before the colon either way.
   */
  const nextValues = next ? { title: next.title, day: formatMonthDay(next.occursOn) } : null
  const sub = block
    ? nextValues
      ? t('cohort.stripBlockNext', { block: block.title, ...nextValues })
      : block.title
    : nextValues
      ? t('cohort.stripNext', nextValues)
      : null

  return (
    /*
     * `padding: 20px 24px`, not `--pad-card` (28px). The strip is a summary
     * row rather than a card of content, and at 28px it stood 110px tall
     * against the design's 96.
     */
    <section className="flex flex-wrap items-center gap-5 rounded-[var(--r-lg)] border border-border bg-surface px-6 py-5">
      <span
        aria-hidden
        /*
         * 48px on a quiet Bleu gris mist.
         *
         * The prototype asked for an undeclared `--sage-mist` and rendered its
         * neutral fallback; `--sage-light`, a bright chip green, made the one
         * quiet row on the page the loudest thing on it. `--bleu-gris-light`
         * is that mist on the palette; the glyph is navy on it.
         */
        className="grid h-12 w-12 flex-none place-items-center rounded-full bg-bleu-gris-light text-ink-on-tint"
      >
        <IconStar4 size={20} title={null} />
      </span>

      {/* `.pro-cert-main` — `flex: 1; min-width: 220px`. */}
      <div className="min-w-[220px] flex-1">
        {/*
         * `.pro-cert-main h2` is the SANS face at 700, not the serif display
         * one. Measured off the prototype: `Inter Tight`, 19px, 700. Ours was
         * `font-serif`/500, which read as a section heading — and this row is
         * not a section, it is a status line with a heading's size.
         */}
        <p className="text-[19px] font-bold leading-[1.25] text-ink">
          {t('cohort.stripTitle', {
            name: cohortLabel(programme),
            month,
            total: programme.months,
          })}
        </p>
        {sub ? <p className="mt-1 text-[13.5px] leading-[1.5] text-slate">{sub}</p> : null}
      </div>

      {total > 0 ? (
        /*
         * `.pro-cert-next` — a dashed rule separates the progress column from
         * the sentence, and it becomes a top rule when the row wraps
         * (`pro.css:140`, at 720px). We had no divider at all, so the three
         * groups read as one run-on row.
         */
        <div className="flex w-full flex-none flex-col gap-1 border-t border-dashed border-border pt-3.5 min-[721px]:w-auto min-[721px]:min-w-[220px] min-[721px]:max-w-[280px] min-[721px]:border-l min-[721px]:border-t-0 min-[721px]:pl-5 min-[721px]:pt-0">
          <p className="text-[11px] font-bold uppercase tracking-[0.05em] text-muted">
            {t('cohort.stripProgress')}
          </p>
          <span aria-hidden className="block h-1.5 overflow-hidden rounded-full bg-progress-track">
            <span
              className="block h-full rounded-full bg-progress"
              style={{ inlineSize: `${Math.round((milestonesComplete / total) * 100)}%` }}
            />
          </span>
          <p className="text-[12px] text-slate">
            {t('cohort.stripMilestones', { count: milestonesComplete, total })}
          </p>
        </div>
      ) : null}

      {/*
       * `.btn-ghost`. 38px in the handoff; 44 here, which is this codebase's
       * standing touch-target floor for every secondary control.
       *
       * The label is the NAV's, not a string of its own. The handoff writes
       * `Cohort journey` here against `Cohort Journey` in the rail, and a
       * second key for one letter of casing bought immediate drift: French got
       * `Parcours de cohorte` beside the rail's `Voyage de cohorte` — two names
       * for one destination, and the wrong one of the two, since this product's
       * whole vocabulary is the boat.
       */}
      <Link
        to="/cohort"
        className="inline-flex min-h-[44px] flex-none items-center gap-1 rounded-full border border-border px-[18px] text-[13.5px] font-semibold text-ink transition-colors hover:border-ink-soft"
      >
        {t('nav.cohortJourney')}
        <IconChevronRight size={14} title={null} />
      </Link>
    </section>
  )
}
